import { randomUUID } from "node:crypto";
import fs from "node:fs/promises";
import path from "node:path";

const SESSION_DIR = ".skaile";
const SESSIONS_SUBDIR = "sessions";
const CURRENT_FILE = "current";
const LEGACY_FILE = "session.json";

/**
 * Runner session metadata.
 *
 * Holds only what the runner needs to resume the agent driver across
 * process restarts — notably `driverSessionId` for SDK session resume.
 *
 * Flow execution state (per-node status, approvals, outputs) moved out
 * of the session file in Phase 2 of the flow-execution plan. It now
 * lives in the FlowAdapter's in-memory snapshot and, for hosts that
 * persist it, in whatever durable store they provide (the Skaile
 * platform uses the `FlowExecution` Prisma table). The runner does
 * not read or write flow state from disk.
 * @docLink packages/runner/session#session-state
 */
export interface SessionState {
  /** Unique identifier for this session run (UUID). */
  runId: string;
  /** ID of the flow definition being executed. */
  flowId: string;
  /** Absolute path to the project directory. */
  projectDir: string;
  /**
   * Absolute path to the agent definition directory (`agent.yaml`,
   * `SOUL.md`, `RULES.md`, `knowledge/`). Resolved from `skaile.yaml`
   * via `resolveAgentDir` when absent at session creation time.
   */
  agentDir?: string;
  /** Agent driver backend used for this session (e.g. `"omp"`, `"claude-sdk"`). */
  driver: string;
  /** LLM provider (if set), e.g. `"anthropic"` or `"openrouter"`. */
  provider?: string;
  /** Model name (if set), e.g. `"claude-opus-4-5"`. */
  model?: string;
  /** Provider-native session/thread identifier for resume support. */
  driverSessionId?: string;
  /** ISO 8601 timestamp when the session was created. */
  startedAt: string;
  /** ISO 8601 timestamp of the last state change. */
  updatedAt: string;
  /** Optional human-readable label for the session */
  label?: string;
  /** Session lifecycle status. */
  status?: "running" | "complete" | "failed" | "paused";
}

// ---------------------------------------------------------------------------
// Path helpers
// ---------------------------------------------------------------------------

function sessionsDir(projectDir: string): string {
  return path.join(projectDir, SESSION_DIR, SESSIONS_SUBDIR);
}

function sessionFilePath(projectDir: string, runId: string): string {
  return path.join(sessionsDir(projectDir), `${runId}.json`);
}

function currentPointerPath(projectDir: string): string {
  return path.join(projectDir, SESSION_DIR, CURRENT_FILE);
}

function legacySessionPath(projectDir: string): string {
  return path.join(projectDir, SESSION_DIR, LEGACY_FILE);
}

// ---------------------------------------------------------------------------
// Migration
// ---------------------------------------------------------------------------

/**
 * Migrate legacy session.json to sessions/<runId>.json if it exists.
 * Safe to call multiple times — no-op if already migrated.
 */
async function migrateLegacySession(projectDir: string): Promise<void> {
  const legacyPath = legacySessionPath(projectDir);
  try {
    const raw = await fs.readFile(legacyPath, "utf-8");
    const state = JSON.parse(raw) as SessionState;
    // Write to new location
    await fs.mkdir(sessionsDir(projectDir), { recursive: true });
    await fs.writeFile(
      sessionFilePath(projectDir, state.runId),
      JSON.stringify(state, null, 2),
      "utf-8",
    );
    // Write current pointer
    await fs.writeFile(currentPointerPath(projectDir), state.runId, "utf-8");
    // Remove legacy file
    await fs.rm(legacyPath);
  } catch {
    // No legacy file or already migrated — ignore
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Load the current (most recently active) session for a project.
 *
 * Reads the `<projectDir>/.skaile/current` pointer file and loads the
 * corresponding session. Auto-migrates the legacy flat `session.json`
 * format to the per-run `sessions/<runId>.json` layout on first call.
 *
 * @param projectDir - Absolute path to the project directory.
 * @returns The current session or `null` if no session exists.
 * @docLink packages/runner/session#load-session
 */
export async function loadSession(projectDir: string): Promise<SessionState | null> {
  await migrateLegacySession(projectDir);
  try {
    const runId = (await fs.readFile(currentPointerPath(projectDir), "utf-8")).trim();
    if (!runId) return null;
    return await loadSessionById(projectDir, runId);
  } catch {
    return null;
  }
}

/**
 * Load a specific session by its run ID.
 *
 * @param projectDir - Absolute path to the project directory.
 * @param runId - The UUID of the session to load.
 * @returns The session state or `null` if not found.
 * @docLink packages/runner/session#load-session-by-id
 */
export async function loadSessionById(
  projectDir: string,
  runId: string,
): Promise<SessionState | null> {
  try {
    const raw = await fs.readFile(sessionFilePath(projectDir, runId), "utf-8");
    return JSON.parse(raw) as SessionState;
  } catch {
    return null;
  }
}

/**
 * List all sessions for a project.
 *
 * Auto-migrates the legacy flat `session.json` format on first call.
 * Corrupt or unreadable session files are silently skipped.
 *
 * @param projectDir - Absolute path to the project directory.
 * @returns All sessions sorted by `updatedAt` descending (newest first).
 * @docLink packages/runner/session#list-sessions
 */
export async function listSessions(projectDir: string): Promise<SessionState[]> {
  await migrateLegacySession(projectDir);
  const dir = sessionsDir(projectDir);
  try {
    const files = await fs.readdir(dir);
    const sessions: SessionState[] = [];
    for (const file of files) {
      if (!file.endsWith(".json")) continue;
      try {
        const raw = await fs.readFile(path.join(dir, file), "utf-8");
        sessions.push(JSON.parse(raw) as SessionState);
      } catch {
        /* skip corrupt files */
      }
    }
    return sessions.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch {
    return [];
  }
}

/**
 * Persist a session to `<projectDir>/.skaile/sessions/<runId>.json`.
 *
 * @param projectDir - Absolute path to the project directory.
 * @param state - Session state to write.
 * @param setCurrent - When `true` (default), updates the `current` pointer
 *   file so subsequent {@link loadSession} calls return this session.
 * @docLink packages/runner/session#save-session
 */
export async function saveSession(
  projectDir: string,
  state: SessionState,
  setCurrent = true,
): Promise<void> {
  await fs.mkdir(sessionsDir(projectDir), { recursive: true });
  await fs.writeFile(
    sessionFilePath(projectDir, state.runId),
    JSON.stringify(state, null, 2),
    "utf-8",
  );
  if (setCurrent) {
    await fs.writeFile(currentPointerPath(projectDir), state.runId, "utf-8");
  }
}

/**
 * Set the current session pointer to an existing session.
 *
 * @param projectDir - Absolute path to the project directory.
 * @param runId - The UUID of the session to make current.
 * @throws {Error} When no session with the given `runId` exists.
 * @docLink packages/runner/session#set-current-session
 */
export async function setCurrentSession(projectDir: string, runId: string): Promise<void> {
  const exists = await loadSessionById(projectDir, runId);
  if (!exists) throw new Error(`Session not found: ${runId}`);
  await fs.mkdir(path.join(projectDir, SESSION_DIR), { recursive: true });
  await fs.writeFile(currentPointerPath(projectDir), runId, "utf-8");
}

/**
 * Delete a session file by run ID.
 *
 * If the deleted session was the current session, the `current` pointer
 * file is also removed. Missing session files are silently ignored.
 *
 * @param projectDir - Absolute path to the project directory.
 * @param runId - The UUID of the session to delete.
 * @docLink packages/runner/session#delete-session
 */
export async function deleteSession(projectDir: string, runId: string): Promise<void> {
  try {
    await fs.rm(sessionFilePath(projectDir, runId));
  } catch {
    /* ignore missing */
  }
  // Clear current pointer if it pointed to this session
  try {
    const current = (await fs.readFile(currentPointerPath(projectDir), "utf-8")).trim();
    if (current === runId) {
      await fs.rm(currentPointerPath(projectDir));
    }
  } catch {
    /* ignore */
  }
}

/**
 * Unset the current session pointer without deleting the session file.
 *
 * After calling this, {@link loadSession} returns `null` until a new
 * session is saved or {@link setCurrentSession} is called.
 *
 * @param projectDir - Absolute path to the project directory.
 * @docLink packages/runner/session#clear-session
 */
export async function clearSession(projectDir: string): Promise<void> {
  try {
    await fs.rm(currentPointerPath(projectDir));
  } catch {
    /* ignore missing */
  }
}

/**
 * Create a new {@link SessionState} with a fresh UUID and current timestamps.
 *
 * The returned session has `status: "running"` and both `startedAt` and
 * `updatedAt` set to the current ISO 8601 time. Caller is responsible for
 * persisting it via {@link saveSession}.
 *
 * @param opts - All session fields except `runId`, `startedAt`, `updatedAt`,
 *   and `status` (which are generated automatically).
 * @returns A fully initialized session state ready to be saved.
 * @docLink packages/runner/session#new-session
 */
export function newSession(
  opts: Omit<SessionState, "runId" | "startedAt" | "updatedAt" | "status">,
): SessionState {
  const now = new Date().toISOString();
  return { ...opts, runId: randomUUID(), startedAt: now, updatedAt: now, status: "running" };
}

/**
 * Update a session's `updatedAt` timestamp without changing any other field.
 * Used by callers that persist a session mid-run to refresh the staleness clock.
 *
 * @param state - Current session state
 * @returns New session state with updated timestamp
 * @docLink packages/runner/session#touch-session
 */
export function touchSession(state: SessionState): SessionState {
  return { ...state, updatedAt: new Date().toISOString() };
}
